import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import plannedEventService, {
  translateDayOfWeek,
} from '../../services/plannedEventService';
import { venueService } from '../../services/venueService';
import eventService, { EventTypeDto } from '../../services/eventService';
import {
  PlannedRecurringEvent,
  CreatePlannedEventRequest,
  UpdatePlannedEventRequest,
} from '../../types/plannedEvents';
import { Venue } from '../../types/venue';
import { UserDto } from '../../types/user';
import { userService } from '../../services/userService';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
// Egy forrás: a „HH:mm:ss" → „HH:mm" átalakítás a `dateUtils`-ban áll (lásd BL-2).
import { toHourMinute } from '../../utils/dateUtils';

/**
 * A beosztás-szerkesztő összehasonlítható állapota. Az időket SZÖVEGKÉNT (HH:mm) hasonlítjuk —
 * a `Date` objektumban a mai NAP is benne van, és a `TimePicker` új objektumot ad ugyanarra az
 * időpontra, ami hamis „mentetlen módosítást" okozna.
 */
interface PlannedEventSnapshot {
  eventName: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  eventTypeId: string;
  venueId: string;
  maxParticipants: number;
  requiredTables: number;
  description: string;
  isActive: boolean;
  selectedParticipants: string[];
}

/**
 * A megnyitáskori állapot: szerkesztésnél a betöltött beosztás, létrehozásnál az üres űrlap.
 * Ugyanazokat az értékeket képezi le, amiket a feltöltő effekt / a `resetForm()` beállít — ez az
 * alapvonal a mentetlen-változás védelemhez.
 */
function plannedEventBaseline(
  event: PlannedRecurringEvent | undefined,
  preselectedDay?: string
): PlannedEventSnapshot {
  if (!event) {
    return {
      eventName: '',
      dayOfWeek: preselectedDay || 'Monday',
      startTime: '',
      endTime: '',
      eventTypeId: '',
      venueId: '',
      maxParticipants: 10,
      requiredTables: 1,
      description: '',
      isActive: true,
      selectedParticipants: [],
    };
  }
  return {
    eventName: event.eventName,
    dayOfWeek: event.dayOfWeek.charAt(0).toUpperCase() + event.dayOfWeek.slice(1).toLowerCase(),
    startTime: toHourMinute(event.startTime),
    endTime: toHourMinute(event.endTime),
    eventTypeId: event.eventTypeId,
    venueId: event.venueId || '',
    maxParticipants: event.maxParticipants ?? 10,
    requiredTables: event.requiredTables,
    description: event.description || '',
    isActive: event.isActive,
    selectedParticipants: event.participants?.map((p) => p.userId) ?? [],
  };
}

interface PlannedEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  event?: PlannedRecurringEvent;
  preselectedDay?: string;
}

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
];

const PlannedEventDialog: React.FC<PlannedEventDialogProps> = ({
  open,
  onClose,
  onSave,
  event,
  preselectedDay,
}) => {
  const { showSuccess } = useFeedback();
  const confirm = useConfirm();
  const isEditMode = !!event;

  // Form state
  const [eventName, setEventName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState<string>(preselectedDay || 'Monday');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [eventTypeId, setEventTypeId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<number>(10);
  const [requiredTables, setRequiredTables] = useState<number>(1);
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);

  // Data state
  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  /**
   * A megnyitáskori (betöltött vagy üres) állapot, mint alapvonal a mentetlen-változás
   * védelemhez. `null` = még nem készült el a feltöltés → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<PlannedEventSnapshot | null>(null);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [venuesData, eventTypesData, usersResult] = await Promise.all([
          venueService.getActiveVenues(),
          eventService.getEventTypes(),
          userService.getUsers({ isActive: true, isApproved: true }),
        ]);
        setVenues(venuesData);
        setEventTypes(eventTypesData);
        setUsers(usersResult.data);
      } catch (error) {
        console.error('Error loading data:', error);
        // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
        // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
        setError(requestErrorMessage(error, 'Hiba történt az adatok betöltése során'));
      } finally {
        setLoadingData(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [open]);

  // Load event data when editing
  useEffect(() => {
    if (event && open) {
      setEventName(event.eventName);
      setDayOfWeek(event.dayOfWeek.charAt(0).toUpperCase() + event.dayOfWeek.slice(1).toLowerCase());

      // Parse time strings to Date objects
      const [startHour, startMinute] = event.startTime.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(startHour, startMinute, 0, 0);
      setStartTime(startDate);

      const [endHour, endMinute] = event.endTime.split(':').map(Number);
      const endDate = new Date();
      endDate.setHours(endHour, endMinute, 0, 0);
      setEndTime(endDate);

      setEventTypeId(event.eventTypeId);
      setVenueId(event.venueId || '');
      setMaxParticipants(event.maxParticipants ?? 10);
      setRequiredTables(event.requiredTables);
      setDescription(event.description || '');
      setIsActive(event.isActive);
      setSelectedParticipants(event.participants?.map((p) => p.userId) ?? []);
      // Alapvonal a feltöltés UTÁN — a betöltött beosztás önmagában nem módosítás.
      setBaseline(plannedEventBaseline(event, preselectedDay));
    } else if (!event && open) {
      // Reset form for create mode
      resetForm();
      setBaseline(plannedEventBaseline(undefined, preselectedDay));
    } else if (!open) {
      setBaseline(null);
    }
  }, [event, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setEventName('');
    setDayOfWeek(preselectedDay || 'Monday');
    setStartTime(null);
    setEndTime(null);
    setEventTypeId('');
    setVenueId('');
    setMaxParticipants(10);
    setRequiredTables(1);
    setDescription('');
    setIsActive(true);
    setSelectedParticipants([]);
    setError('');
  };

  const formatTimeToString = (date: Date | null): string => {
    if (!date) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const validateForm = (): boolean => {
    if (!eventName.trim()) {
      setError('Az esemény neve kötelező');
      return false;
    }
    if (!startTime) {
      setError('A kezdési idő kötelező');
      return false;
    }
    if (!endTime) {
      setError('A befejezési idő kötelező');
      return false;
    }
    if (endTime <= startTime) {
      setError('A befejezési időnek későbbinek kell lennie, mint a kezdési idő');
      return false;
    }
    if (!eventTypeId) {
      setError('Az esemény típusa kötelező');
      return false;
    }
    if (maxParticipants < 1) {
      setError('A maximális létszámnak legalább 1-nek kell lennie');
      return false;
    }
    if (requiredTables < 1) {
      setError('A szükséges asztalok számának legalább 1-nek kell lennie');
      return false;
    }
    if (selectedParticipants.length > maxParticipants) {
      setError(`A résztvevők száma (${selectedParticipants.length}) meghaladja a maximális létszámot (${maxParticipants})`);
      return false;
    }
    setError('');
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError('');

      const requestData = {
        eventName: eventName.trim(),
        dayOfWeek: dayOfWeek,
        startTime: formatTimeToString(startTime),
        endTime: formatTimeToString(endTime),
        eventTypeId,
        venueId: venueId || null,
        maxParticipants,
        requiredTables,
        description: description.trim() || null,
        isActive,
      };

      if (isEditMode && event) {
        // Update existing event
        await plannedEventService.updatePlannedEvent(
          event.plannedEventId,
          requestData as UpdatePlannedEventRequest
        );

        // Update participants
        const currentParticipantIds = event.participants?.map((p) => p.userId) ?? [];
        const participantsToAdd = selectedParticipants.filter(
          (id) => !currentParticipantIds.includes(id)
        );
        const participantsToRemove = currentParticipantIds.filter(
          (id) => !selectedParticipants.includes(id)
        );

        // Add new participants
        for (const userId of participantsToAdd) {
          await plannedEventService.addParticipant(event.plannedEventId, userId);
        }

        // Remove participants
        for (const userId of participantsToRemove) {
          await plannedEventService.removeParticipant(event.plannedEventId, userId);
        }
      } else {
        // Create new event
        const createdEvent = await plannedEventService.createPlannedEvent(
          requestData as CreatePlannedEventRequest
        );

        // Add participants
        for (const userId of selectedParticipants) {
          await plannedEventService.addParticipant(createdEvent.plannedEventId, userId);
        }
      }

      showSuccess('A beosztás mentve.');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving planned event:', error);
      // [validátor M-1 / 3. javító kör F-1, 2026-10-03] lejárt munkamenetnél az axios angol „Request
      // failed with status code 401" helyett az újra-belépő ablakkal egyező mondat. ⚠️ A 2. kör
      // változata itt HATÁSTALAN volt: a `plannedEventService.handleError` sima `Error`-ba csomagolt,
      // `response` nélkül — ma a csomagolt hiba megőrzi a választ, és KÉRÉS-hibánál a közös
      // `saveErrorMessage` dönt (a szerver magyar indoklása / státuszkód / hálózati mondat, az angol
      // „… failed: …" előtag nélkül); a nem-kérés hiba saját szövege marad.
      const requestFailed = !!(error as any)?.response || !!(error as any)?.request;
      setError(
        requestFailed
          ? saveErrorMessage(error, 'Hiba történt az esemény mentése során')
          : error instanceof Error
            ? error.message
            : 'Hiba történt az esemény mentése során'
      );
    } finally {
      setSaving(false);
    }
  };

  const currentSnapshot: PlannedEventSnapshot = {
    eventName,
    dayOfWeek,
    startTime: formatTimeToString(startTime),
    endTime: formatTimeToString(endTime),
    eventTypeId,
    venueId,
    maxParticipants,
    requiredTables,
    description,
    isActive,
    selectedParticipants,
  };
  // `open &&`: a dialógus zárva is mountolva marad a szülőben.
  const isDirty = open && isFormDirty(baseline, currentSnapshot);
  // A BEJELENTETT NÉV = a FEJLÉC szövege [N-16, 2026-09-14]: létrehozásnál a „Beosztás
  // szerkesztése" név hazudott (a felhasználó „Új Esemény Létrehozása" fejlécet lát).
  useUnsavedChanges(isDirty, isEditMode ? 'Esemény Szerkesztése' : 'Új Esemény Létrehozása');

  /** A Dialog `onClose`-a (backdrop + ESC) és a „Mégse" gomb közös, védett záró útja. */
  const handleClose = async () => {
    if (saving) return;
    if (isDirty && !(await confirm(discardChangesConfirm('beosztás szerkesztője')))) {
      return;
    }
    resetForm();
    setBaseline(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="planned-event-dialog-title"
    >
      <DialogTitle id="planned-event-dialog-title">
        {isEditMode ? 'Esemény Szerkesztése' : 'Új Esemény Létrehozása'}
      </DialogTitle>

      <DialogContent dividers>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Event Name */}
            <TextField
              label="Esemény Neve"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              required
              fullWidth
              autoFocus
            />

            {/* Day of Week */}
            <FormControl fullWidth required>
              <InputLabel>Nap</InputLabel>
              <Select
                value={dayOfWeek}
                label="Nap"
                onChange={(e) => setDayOfWeek(e.target.value)}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day} value={day}>
                    {translateDayOfWeek(day)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Time Pickers */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu} localeText={HU_PICKER_LOCALE_TEXT}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TimePicker
                  label="Kezdési Idő"
                  value={startTime}
                  onChange={(newValue) => setStartTime(newValue as Date | null)}
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
                <TimePicker
                  label="Befejezési Idő"
                  value={endTime}
                  onChange={(newValue) => setEndTime(newValue as Date | null)}
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>

            {/* Event Type */}
            <FormControl fullWidth required>
              <InputLabel>Esemény Típusa</InputLabel>
              <Select
                value={eventTypeId}
                label="Esemény Típusa"
                onChange={(e) => setEventTypeId(e.target.value)}
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type.eventTypeId} value={type.eventTypeId}>
                    {type.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Venue */}
            <FormControl fullWidth>
              <InputLabel>Helyszín</InputLabel>
              <Select
                value={venueId}
                label="Helyszín"
                onChange={(e) => setVenueId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Nincs megadva</em>
                </MenuItem>
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Max Participants and Required Tables */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Maximális Létszám"
                type="number"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(Number(e.target.value))}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Szükséges Asztalok"
                type="number"
                value={requiredTables}
                onChange={(e) => setRequiredTables(Number(e.target.value))}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Box>

            {/* Participants */}
            <Autocomplete
              multiple
              options={users}
              value={users.filter((u) => selectedParticipants.includes(u.userId))}
              onChange={(_, newValue) => {
                setSelectedParticipants(newValue.map((u) => u.userId));
              }}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Résztvevők"
                  placeholder="Válassz felhasználókat..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.name}
                      {...chipProps}
                      size="small"
                    />
                  );
                })
              }
            />

            {selectedParticipants.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Kiválasztott résztvevők: {selectedParticipants.length} / {maxParticipants}
              </Typography>
            )}

            {/* Description */}
            <TextField
              label="Leírás"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            {/* Active Status (only in edit mode) */}
            {isEditMode && (
              <FormControl fullWidth>
                <InputLabel>Állapot</InputLabel>
                <Select
                  value={isActive ? 'active' : 'inactive'}
                  label="Állapot"
                  onChange={(e) => setIsActive(e.target.value === 'active')}
                >
                  <MenuItem value="active">Aktív</MenuItem>
                  <MenuItem value="inactive">Inaktív</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Mégse
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || loadingData}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? 'Mentés...' : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlannedEventDialog;
